<template>
  <div class="bb-overview">
    <BbScoreBar />

    <div class="content">
      <header class="round-head">
        <h1 class="title">Ronde {{ activeRoundNumber }} <span class="of">van {{ totalRounds }}</span></h1>
        <p class="subtitle">
          {{ activeCards }} {{ activeCards === 1 ? 'slag' : 'slagen' }} te verdelen
          <span v-if="trump" class="trump-chip" :class="{ red: trump.red }">
            {{ trump.symbol }} {{ trump.label }}
          </span>
        </p>
      </header>

      <BbPlayerGrid class="bids" tag="ol" :count="biddingOrder.length">
        <li
          v-for="(player, index) in biddingOrder"
          :key="player.id"
          class="bid-row"
        >
          <span class="bid-head">
            <span class="bid-order">{{ index + 1 }}</span>
            <span class="bid-name">{{ player.name }}</span>
            <span v-if="player.id === activeDealerId" class="dealer-tag">Deler</span>
          </span>
          <span class="bid-number">{{ bids[player.id] ?? 0 }}</span>
        </li>
      </BbPlayerGrid>

      <p class="total-line">
        Samen {{ totalBid }} van {{ activeCards }} {{ activeCards === 1 ? 'slag' : 'slagen' }} geboden —
        <strong>{{ totalBid > activeCards ? 'overboden' : 'onderboden' }}</strong>
      </p>
    </div>

    <AppFooter
      :show-back="true"
      @continue="goToResult"
      @back="backToBidding"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBoerenbridgeStore } from '../../stores/boerenbridge'
import { trumpById } from '../../models/boerenbridge'
import BbScoreBar from './BbScoreBar.vue'
import BbPlayerGrid from './BbPlayerGrid.vue'
import AppFooter from '../shared/AppFooter.vue'

export default {
  name: 'BbBidOverview',
  components: { BbScoreBar, BbPlayerGrid, AppFooter },
  computed: {
    ...mapState(useBoerenbridgeStore, [
      'players',
      'biddingOrder',
      'activeRoundNumber',
      'activeCards',
      'activeDealerId',
      'totalRounds',
      'currentBid'
    ]),
    bids() {
      return this.currentBid?.bids ?? {}
    },
    trump() {
      return trumpById(this.currentBid?.trump)
    },
    totalBid() {
      return this.players.reduce((sum, p) => sum + (this.bids[p.id] ?? 0), 0)
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    ...mapActions(useBoerenbridgeStore, ['goToResult', 'backToBidding']),
    handleKeydown(e) {
      if (e.key === 'Enter') this.goToResult()
      if (e.key === 'Escape') this.backToBidding()
    }
  }
}
</script>

<style scoped>
.bb-overview {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  gap: 1rem;
}

.round-head { text-align: center; }

.title {
  font-size: 1.5rem;
  color: #2c3e50;
}

.of {
  font-size: 1rem;
  font-weight: 400;
  color: #5a6d5a;
}

.subtitle {
  font-size: 0.9rem;
  color: #5a6d5a;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.trump-chip {
  font-size: 0.8rem;
  font-weight: 600;
  color: #2c3e50;
  background: #eef5ee;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
}

.trump-chip.red { color: #c0392b; }

.bid-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border: 2px solid #aac8aa;
  border-radius: 12px;
}

.bid-head {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bid-order {
  flex: 0 0 auto;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #7a9a7a;
  background: #eef5ee;
  border-radius: 50%;
}

.bid-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dealer-tag {
  flex: 0 0 auto;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  background: #2c3e50;
  border-radius: 999px;
  padding: 0.15rem 0.45rem;
}

.bid-number {
  flex: 0 0 auto;
  min-width: 2.5rem;
  padding: 0.15rem 0.5rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  background: #2c3e50;
  border-radius: 8px;
}

.total-line {
  font-size: 0.9rem;
  color: #5a6d5a;
  text-align: center;
}

/* Op tablet staan de biedingen in een raster dat bij het aantal spelers past
   (2×2 bij vier, 3×3 bij negen), groot genoeg om van op afstand te lezen
   terwijl de hand gespeeld wordt. */
@media (min-width: 600px) {
  .content {
    padding: 1.5rem;
    gap: 1.5rem;
  }

  .title { font-size: 2rem; }

  .subtitle { font-size: 1.05rem; }

  .trump-chip {
    font-size: 0.95rem;
    padding: 0.2rem 0.65rem;
  }

  .bids { max-width: 64rem; }

  /* Naam en bod schalen samen mee, zodat de naam leidend blijft */
  .bid-row {
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    border-radius: 14px;
  }

  .bid-head { gap: 0.75rem; }

  .bid-order {
    width: 2rem;
    height: 2rem;
    font-size: 0.85rem;
  }

  .bid-name { font-size: 1.6rem; }

  .dealer-tag {
    font-size: 0.7rem;
    padding: 0.2rem 0.55rem;
  }

  .bid-number {
    min-width: 3.5rem;
    padding: 0.2rem 0.75rem;
    font-size: 2rem;
    border-radius: 10px;
  }

  .total-line { font-size: 1.05rem; }
}
</style>
